import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import { toast } from "sonner";
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock,
  Award,
  Share2,
  ExternalLink,
  Search,
  Loader2,
  UserPlus,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Eye,
  Gift
} from "lucide-react";
import { API } from "../../apiConfig";


const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
};

const AmbassadorManagementTab = ({ token }) => {
  const [applications, setApplications] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, postsRes, ambassadorsRes] = await Promise.all([
        axios.get(`${API}/admin/ambassador-applications`, { headers }).catch(() => ({ data: { applications: [] } })),
        axios.get(`${API}/points/admin/pending-social-posts`, { headers }).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/admin/ambassadors`, { headers }).catch(() => ({ data: { ambassadors: [] } }))
      ]);
      
      setApplications(appsRes.data.applications || []);
      setSocialPosts(postsRes.data.posts || []);
      setAmbassadors(ambassadorsRes.data.ambassadors || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationAction = async (applicationId, action) => {
    setProcessing(applicationId);
    try {
      await axios.post(`${API}/admin/ambassador-applications/${applicationId}/${action}`, {}, { headers });
      toast.success(`Application ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} application`);
    } finally {
      setProcessing(null);
      setSelectedApp(null);
    }
  };

  const handleSocialPostAction = async (submissionId, action) => {
    setProcessing(submissionId);
    try {
      await axios.post(`${API}/points/admin/review-social-post/${submissionId}`, 
        { action }, 
        { headers }
      );
      toast.success(`Post ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} post`);
    } finally {
      setProcessing(null);
      setSelectedPost(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{ambassadors.length}</p>
                <p className="text-sm text-muted-foreground">Active Ambassadors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <UserPlus className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{applications.filter(a => a.status === "pending").length}</p>
                <p className="text-sm text-muted-foreground">Pending Applications</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Share2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{socialPosts.length}</p>
                <p className="text-sm text-muted-foreground">Posts to Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">25</p>
                <p className="text-sm text-muted-foreground">Points per Post</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="applications" className="space-y-4">
        <TabsList>
          <TabsTrigger value="applications" className="gap-2">
            <UserPlus className="w-4 h-4" />
            Applications
            {applications.filter(a => a.status === "pending").length > 0 && (
              <Badge className="ml-1 bg-amber-500">{applications.filter(a => a.status === "pending").length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="posts" className="gap-2">
            <Share2 className="w-4 h-4" />
            Social Posts
            {socialPosts.length > 0 && (
              <Badge className="ml-1 bg-blue-500">{socialPosts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ambassadors" className="gap-2">
            <Award className="w-4 h-4" />
            All Ambassadors
          </TabsTrigger>
        </TabsList>

        {/* Applications Tab */}
        <TabsContent value="applications">
          <Card>
            <CardHeader>
              <CardTitle>Ambassador Applications</CardTitle>
              <CardDescription>Review and approve new ambassador applications</CardDescription>
            </CardHeader>
            <CardContent>
              {applications.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No applications yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Social Media</TableHead>
                      <TableHead>Audience</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.application_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{app.user_name}</p>
                            <p className="text-sm text-muted-foreground">{app.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {app.social_media || "Not provided"}
                        </TableCell>
                        <TableCell>{app.followers || "N/A"}</TableCell>
                        <TableCell>
                          {new Date(app.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedApp(app)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {app.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApplicationAction(app.application_id, "approve")}
                                  disabled={processing === app.application_id}
                                >
                                  {processing === app.application_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleApplicationAction(app.application_id, "reject")}
                                  disabled={processing === app.application_id}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Posts Tab */}
        <TabsContent value="posts">
          <Card>
            <CardHeader>
              <CardTitle>Social Post Submissions</CardTitle>
              <CardDescription>Review and approve social media posts for points</CardDescription>
            </CardHeader>
            <CardContent>
              {socialPosts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pending posts to review</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ambassador</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Post URL</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {socialPosts.map((post) => {
                      const PlatformIcon = PLATFORM_ICONS[post.platform] || Share2;
                      return (
                        <TableRow key={post.submission_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{post.user_name}</p>
                              <p className="text-sm text-muted-foreground">{post.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              <PlatformIcon className="w-3 h-3 mr-1" />
                              {post.platform}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <a 
                              href={post.post_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              View Post <ExternalLink className="w-3 h-3" />
                            </a>
                          </TableCell>
                          <TableCell>
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleSocialPostAction(post.submission_id, "approve")}
                                disabled={processing === post.submission_id}
                              >
                                {processing === post.submission_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleSocialPostAction(post.submission_id, "reject")}
                                disabled={processing === post.submission_id}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Ambassadors Tab */}
        <TabsContent value="ambassadors">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Ambassadors</CardTitle>
                  <CardDescription>View and manage all active ambassadors</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search ambassadors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {ambassadors.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No ambassadors yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ambassador</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Referrals</TableHead>
                      <TableHead>Total Sales</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Tier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ambassadors
                      .filter(a => 
                        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        a.email?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((ambassador) => (
                        <TableRow key={ambassador.user_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ambassador.name}</p>
                              <p className="text-sm text-muted-foreground">{ambassador.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="px-2 py-1 bg-muted rounded">
                              {ambassador.referral_code || "N/A"}
                            </code>
                          </TableCell>
                          <TableCell>{ambassador.total_referrals || 0}</TableCell>
                          <TableCell>${(ambassador.total_sales || 0).toFixed(2)}</TableCell>
                          <TableCell>{ambassador.points_balance || 0}</TableCell>
                          <TableCell>
                            <Badge className={
                              ambassador.tier === "platinum" ? "bg-purple-500" :
                              ambassador.tier === "gold" ? "bg-yellow-500" :
                              ambassador.tier === "silver" ? "bg-gray-400" :
                              "bg-amber-600"
                            }>
                              {ambassador.tier || "Bronze"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Application Detail Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Applicant</p>
                <p className="font-medium">{selectedApp.user_name}</p>
                <p className="text-sm">{selectedApp.user_email}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Social Media</p>
                <p>{selectedApp.social_media || "Not provided"}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Audience Size</p>
                <p>{selectedApp.followers || "Not specified"}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Experience with Aboriginal Art</p>
                <p className="text-sm">{selectedApp.experience || "Not provided"}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Motivation</p>
                <p className="text-sm">{selectedApp.motivation || "Not provided"}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                {getStatusBadge(selectedApp.status)}
              </div>
            </div>
          )}
          {selectedApp?.status === "pending" && (
            <DialogFooter>
              <Button
                variant="destructive"
                onClick={() => handleApplicationAction(selectedApp.application_id, "reject")}
                disabled={processing === selectedApp?.application_id}
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleApplicationAction(selectedApp.application_id, "approve")}
                disabled={processing === selectedApp?.application_id}
              >
                {processing === selectedApp?.application_id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmbassadorManagementTab;
